import useGetMe from '@/api/queries/me/useGetMe';
import useGetMyAccountBranding from '@/api/queries/me/useGetMyAccountBranding';
import { useSelector } from '@/api/state';
import useLogout from '@/hooks/auth/useLogout';
import { usePermissions } from '@/hooks/usePermissions';
import { useShowParentAccountAdminLayout } from '@/hooks/useShowParentAccountAdminLayout';
import {
  ButtonDropdown,
  TDropdownItem,
  TDropdownSection,
} from '@payaca/ambrosia';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { CustomersPermissions } from '@payaca/permissions/customers/customers.permissions';
import { LineItemsPermissions } from '@payaca/permissions/lineItems/line-items.permissions';
import { MaterialsPermissions } from '@payaca/permissions/materials/materials.permissions';
import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import { SuppliersPermissions } from '@payaca/permissions/suppliers/suppliers.permissions';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useMemo } from 'react';
import { useHistory } from 'react-router';

const CompanyDropdown: FC = () => {
  const history = useHistory();
  const { user } = useGetMe();
  const { data: branding } = useGetMyAccountBranding();
  const myProfile = useSelector((state: any) => state.users.myProfile);
  const { userHasRequiredPermission, userHasAllRequiredPermissions } =
    usePermissions();
  const { showParentAccountAdminLayout } = useShowParentAccountAdminLayout();
  const logout = useLogout();

  const accountAccessInformation = useSelector(
    (state) => state.account.accountAccessInformation
  );

  const trialPeriodIsExpired = useMemo(() => {
    return accountAccessInformation
      ? !accountAccessInformation.hasActiveSubscription &&
          accountAccessInformation.isTrialPeriodExpired
      : false;
  }, [accountAccessInformation]);

  const hasExceededUserSeats = useMemo(() => {
    if (!accountAccessInformation) return false;
    return (
      accountAccessInformation.hasActiveSubscription &&
      accountAccessInformation.hasExceededActiveSubscriptionUserSeats
    );
  }, [accountAccessInformation]);

  const hasExpiredConnection = useMemo(() => {
    const hasExpiredXeroConnection =
      !!myProfile?.accounts?.[0]?.integrations?.xero?.hasRefreshTokenExpired;
    const oAuthConnections = myProfile?.oAuthConnections || [];
    const zapierConnection = oAuthConnections.find(
      (p: any) => p.clientName === 'Zapier' || p.clientName === 'zapier test'
    );
    const hasExpiredZapierConnection =
      zapierConnection && zapierConnection.hasConnectionExpired;
    return hasExpiredXeroConnection || hasExpiredZapierConnection;
  }, [myProfile]);

  const companyName = user?.account?.name;
  const logoUrl = branding?.logoUrl;

  const dropdownItems = useMemo((): TDropdownSection[] => {
    if (showParentAccountAdminLayout) {
      const items: TDropdownItem[] = [];

      if (userHasRequiredPermission([AccountsPermissions.GET_DETAILED_USERS])) {
        items.push({
          label: 'Users',
          onClick: () => history.push('/users'),
          disabled: trialPeriodIsExpired,
        });
      }

      items.push({
        label: 'Log out',
        onClick: logout,
      });

      return [{ items }];
    }

    const settingsItems: TDropdownItem[] = [
      {
        label: 'Profile',
        onClick: () => history.push('/personalSettings'),
      },
    ];

    if (userHasRequiredPermission([AccountsPermissions.UPDATE_ACCOUNT])) {
      settingsItems.push({
        label: 'My company',
        onClick: () => history.push('/companySettings'),
        disabled: trialPeriodIsExpired || hasExceededUserSeats,
      });
    }

    if (userHasRequiredPermission([AccountsPermissions.GET_DETAILED_USERS])) {
      settingsItems.push({
        label: 'Users',
        onClick: () => history.push('/users'),
        disabled: trialPeriodIsExpired,
      });
    }

    if (userHasRequiredPermission([AccountsPermissions.UPDATE_SUBSCRIPTION])) {
      settingsItems.push({
        label: 'Manage subscription',
        onClick: () => history.push('/upgradeAccount'),
      });
    }

    if (
      userHasRequiredPermission([
        TemplatesPermissions.GET_COLLECTION_MANAGEMENT_VIEW,
      ])
    ) {
      settingsItems.push({
        label: 'Templates',
        onClick: () => history.push('/templates'),
      });
    }

    if (userHasRequiredPermission([AccountsPermissions.MANAGE_TAGS])) {
      settingsItems.push({
        label: 'Tags',
        onClick: () => history.push('/tags'),
      });
    }

    const dataItems: TDropdownItem[] = [];

    if (
      userHasAllRequiredPermissions([
        CustomersPermissions.ADD_CUSTOMER,
        LineItemsPermissions.ADD_LINE_ITEM,
        MaterialsPermissions.PERSIST_MATERIAL,
        ScheduledEventsPermissions.ADD_EVENT,
        SuppliersPermissions.PERSIST_SUPPLIER,
      ])
    ) {
      dataItems.push({
        label: 'Import data',
        onClick: () => history.push('/csv-uploads'),
      });
    }

    if (
      userHasRequiredPermission([
        AccountsPermissions.UPDATE_CONNECTION,
        AccountsPermissions.UPDATE_CONNECTION_ACCOUNTING,
        AccountsPermissions.UPDATE_CONNECTION_SUPPLIERS,
        AccountsPermissions.UPDATE_CONNECTION_PAYMENT,
        AccountsPermissions.UPDATE_CONNECTION_AUTOMATION,
      ])
    ) {
      dataItems.push({
        label: hasExpiredConnection ? 'Connections (!)' : 'Connections',
        onClick: () => history.push('/connections'),
        disabled: trialPeriodIsExpired || hasExceededUserSeats,
      });
    }

    const sections: TDropdownSection[] = [{ items: settingsItems }];

    if (dataItems.length > 0) {
      sections.push({ items: dataItems });
    }

    sections.push({
      items: [{ label: 'Log out', onClick: logout }],
    });

    return sections;
  }, [
    userHasRequiredPermission,
    userHasAllRequiredPermissions,
    showParentAccountAdminLayout,
    trialPeriodIsExpired,
    hasExceededUserSeats,
    hasExpiredConnection,
    history,
    logout,
  ]);

  return (
    <ButtonDropdown
      variant="ghost"
      colour="black"
      size="sm"
      dropdownOptions={{
        items: dropdownItems,
        anchor: 'start',
      }}
    >
      {logoUrl && (
        <img
          className="size-8 rounded-full object-cover"
          src={logoUrl}
          alt=""
        />
      )}
      <span className="max-w-24 truncate">{companyName}</span>
      <UntitledIcon name="chevron-selector-vertical" className="size-4" />
    </ButtonDropdown>
  );
};

export default CompanyDropdown;
